/** Single-photo draft shares the canonical oriented decoder and pixel renderer.
 * The fixed crop is identical in preview/export; no network upload occurs until profile save. */
export function createProfileImage(
  ctx,
  tools,
  { label = 'Profile photo', ratio = 1, onReady = () => {} } = {},
) {
  const root = ctx.e('section', 'profile-image-editor');
  const file = ctx.field('input', {
    type: 'file',
    accept: 'image/*,.heic,.heif',
    ariaLabel: `${ctx.uiText(label)}: ${ctx.uiText('Upload image')}`,
  });
  const status = ctx.e('p', 'subtle');
  status.setAttribute('role', 'status');
  const preview = ctx.e('canvas', 'profile-image-preview');
  preview.setAttribute('aria-label', `${ctx.uiText(label)}: ${ctx.uiText('Crop preview')}`);
  const controls = ctx.e('div', 'profile-image-controls');
  let entry,
    disposed = false,
    busy = false,
    generation = 0;
  preview.hidden = true;
  controls.hidden = true;
  function draw() {
    if (entry && !disposed) tools.renderFrame(preview, entry, { ratio, edge: 640 });
  }
  for (const [key, text, min, max, step, value] of [
    ['zoom', 'Scale image', 1, 3, 0.05, 1],
    ['panX', 'Horizontal position', 0, 1, 0.01, 0.5],
    ['panY', 'Vertical position', 0, 1, 0.01, 0.5],
  ]) {
    const input = ctx.field('input', {
      type: 'range',
      min,
      max,
      step,
      value,
      ariaLabel: `${ctx.uiText(label)}: ${ctx.uiText(text)}`,
    });
    input.dataset.cropKey = key;
    input.addEventListener('input', () => {
      if (entry) {
        entry[key] = Number(input.value);
        draw();
      }
    });
    controls.append(
      ctx.append(ctx.e('label', 'profile-image-range'), ctx.e('span', '', ctx.uiText(text)), input),
    );
  }
  function clear() {
    generation++;
    entry?.source.close?.();
    entry = undefined;
    preview.hidden = true;
    controls.hidden = true;
    status.textContent = '';
  }
  file.addEventListener('change', async () => {
    const selected = file.files?.[0];
    if (!selected || busy || disposed) return;
    const token = ++generation;
    busy = true;
    file.disabled = true;
    status.textContent = ctx.uiText('Preparing image…');
    try {
      if (selected.size > 60 * 1024 * 1024 || selected.type.startsWith('video/'))
        throw new Error('Choose a photo under 60 MB.');
      const source = await tools.decodePhoto(selected);
      if (disposed || token !== generation) {
        source.close?.();
        return;
      }
      if (
        (source.width || source.naturalWidth) * (source.height || source.naturalHeight) >
        40_000_000
      ) {
        source.close?.();
        throw new Error('Choose a photo under 40 megapixels.');
      }
      entry?.source.close?.();
      entry = {
        kind: 'image',
        source,
        rotation: 0,
        ratio,
        zoom: 1,
        panX: 0.5,
        panY: 0.5,
        filter: 'original',
        alt: ctx.uiText(label),
      };
      for (const control of controls.querySelectorAll('input'))
        control.value = entry[control.dataset.cropKey];
      draw();
      preview.hidden = false;
      controls.hidden = false;
      status.textContent = ctx.uiText(
        'Crop ready. Adjust scale and position, then save your profile.',
      );
      onReady();
    } catch (error) {
      if (!disposed && token === generation) status.textContent = ctx.uiText(error.message);
    } finally {
      busy = false;
      file.disabled = false;
      file.value = '';
    }
  });
  root.append(file, status, preview, controls);
  return {
    node: root,
    clear,
    hasImage: () => !!entry,
    isBusy: () => busy,
    async exportUpload() {
      if (busy) throw new Error('Wait for image processing.');
      if (!entry) return null;
      const captured = { ...entry };
      const canvas = ctx.e('canvas');
      const size = tools.renderFrame(canvas, captured, { ratio, edge: ratio === 1 ? 640 : 1600 });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      if (!blob || blob.size > tools.limits.photoBytes)
        throw new Error('Could not prepare this image. Try a smaller photo.');
      return { data: await tools.blobData(blob), ...size, alt: captured.alt };
    },
    dispose() {
      disposed = true;
      clear();
    },
  };
}
